import type { FC } from 'react';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { dateToDistanceTrans } from '@wix/da-i18next/pkg/time/functions';
import DeviantArtLogo from '@wix/da-ds/pkg/Icons/DeviantArtLogo';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import ArrowDown from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import ArrowUp from '@wix/da-ds/pkg/Icons/16x16/ArrowUp';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ProfileStatItemNumeric from '../ProfileStatItem/ProfileStatItemNumeric';
import s from './ProfileHeadStats.scss';

interface Props {
  stats: Record<string, number | string | undefined>;
}

const NUMERIC_STATS_DESKTOP = [
  'watchers',
  'friends',
  'deviations',
  'pageviews',
  'favourites',
  'groupAdminships',
  'groupMemberships',
];

const NUMERIC_STATS_MOBILE = [
  'watchers',
  'watching',
  'deviations',
  'pageviews',
  'favourites',
  'groupAdminships',
  'groupMemberships',
];

const BREAK_AFTER_MOBILE = new Set(['deviations', 'favourites']);

const DeviantForLabel: FC<{ joinDate: string }> = ({ joinDate }) => {
  const { t } = useTranslation();
  const distance = dateToDistanceTrans(joinDate);
  return (
    <span className={s['deviant-for']}>
      <DeviantArtLogo disableDefaultColors size={IconSize.SMALLER} className={s['da-logo']} />
      {t('widgets.aboutMe.deviantFor', {
        deviantFor: t(distance.key, { count: distance.value }),
      })}
    </span>
  );
};

const GROUP_STATS = new Set(['groupAdminships', 'groupMemberships']);

const ProfileHeadStats: FC<Props> = ({ stats }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [groupStatsExpanded, setGroupStatsExpanded] = useState(false);
  const hasJoinDate = typeof stats.joinDate === 'string';
  const numericStats = isMobile ? NUMERIC_STATS_MOBILE : NUMERIC_STATS_DESKTOP;
  const allValidStats = numericStats.filter(key => {
    const value = stats[key];
    if (typeof value !== 'number') {
      return false;
    }
    if (key === 'groupAdminships') {
      return value >= 1;
    }
    if (key === 'groupMemberships') {
      return value >= 2;
    }
    return true;
  });

  const mainStats = isMobile ? allValidStats.filter(k => !GROUP_STATS.has(k)) : allValidStats;
  const groupStats = isMobile ? allValidStats.filter(k => GROUP_STATS.has(k)) : [];
  const hasGroupStats = groupStats.length > 0;

  const breakAfterKeys = isMobile
    ? BREAK_AFTER_MOBILE
    : new Set(mainStats.indexOf('deviations') !== -1 ? ['deviations'] : []);

  const lineStartIndices = new Set<number>([0]);
  mainStats.forEach((key, i) => {
    if (breakAfterKeys.has(key) && i + 1 < mainStats.length) {
      lineStartIndices.add(i + 1);
    }
  });

  return (
    <div className={s['root']}>
      {hasJoinDate && (
        <>
          <span className={s['deviant-for-row']}>
            <DeviantForLabel joinDate={stats.joinDate as string} />
            {isMobile && hasGroupStats && (
              <PlainButtonOrLink
                className={s['expand-toggle']}
                aria-label={t(
                  groupStatsExpanded
                    ? 'profileStats.collapseGroupStats'
                    : 'profileStats.expandGroupStats'
                )}
                aria-expanded={groupStatsExpanded}
                onClick={() => setGroupStatsExpanded(prev => !prev)}
              >
                {groupStatsExpanded ? (
                  <ArrowUp disableDefaultColors size={IconSize.SMALLER} />
                ) : (
                  <ArrowDown disableDefaultColors size={IconSize.SMALLER} />
                )}
              </PlainButtonOrLink>
            )}
          </span>
          {isMobile ? (
            <div className={s['line-break']} />
          ) : (
            mainStats.length > 0 && <span className={s['separator']}>•</span>
          )}
        </>
      )}
      {mainStats.map((key, index) => {
        const isLineStart = lineStartIndices.has(index);
        return (
          <Fragment key={key}>
            {index > 0 && !isLineStart && <span className={s['separator']}>•</span>}
            <ProfileStatItemNumeric
              stat={key}
              value={stats[key] as number}
              className={s['stat-item']}
            />
            {breakAfterKeys.has(key) && <div className={s['line-break']} />}
          </Fragment>
        );
      })}
      {isMobile && hasGroupStats && groupStatsExpanded && (
        <>
          <div className={s['line-break']} />
          {groupStats.map((key, index) => (
            <Fragment key={key}>
              {index > 0 && <span className={s['separator']}>•</span>}
              <ProfileStatItemNumeric
                stat={key}
                value={stats[key] as number}
                className={s['stat-item']}
              />
            </Fragment>
          ))}
        </>
      )}
    </div>
  );
};

ProfileHeadStats.displayName = 'ProfileHeadStats';

export default ProfileHeadStats;
